import React, {useEffect, useMemo, useRef, useState} from 'react'
import {ArrowRight, Minus, Plus, ArrowLeft, Maximize2, Circle, Square, Layers, Package} from 'lucide-react'
import {Link, useNavigate} from 'react-router-dom'
import {ImageWithFallback} from './figma/ImageWithFallback'
import {Button} from './ui/button'
import { Badge } from './ui/badge'
import Slider, {type Settings} from 'react-slick'
import type {siteContent} from '../data/siteContent'
import type {Artwork, Product, CanvasShapeOption, ShapeThicknessOption} from '../types'
import {recordReferenceUpload, removeReferenceUpload, fetchTrendingAnalytics} from '../lib/api'
import {useCart} from '../context/CartContext'
import {cn} from './ui/utils'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { Seo } from './Seo'
import { buildArtworkAlt, buildProductAlt } from '../lib/altText'
import { buildArtworkImageSources } from '../lib/artwork'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'
import { VisuallyHidden } from './ui/visually-hidden'
import { NanoGalleryViewer } from './NanoGalleryViewer'
import { convertImperialToMetricLabel } from '../lib/measurements'
import { getPrimaryArtworkImage } from '../lib/artwork'
import { getLastKnownCountry, trackAnalyticsEvent } from '../lib/analytics'
import { useTranslation } from '../context/LanguageContext'
import { useCurrency } from '../context/CurrencyContext'
import { useArtworkImpressions } from '../hooks/useArtworkImpressions'
import {
    CANVAS_SHAPE_OPTIONS,
    DEFAULT_CANVAS_SHAPE,
} from '../constants/productOptions'
import { Helmet } from 'react-helmet-async'
import { deferToLoadAndIdle } from '../lib/idle'

type FeaturedProductConfig = Product

export type AddToCartPayload = {
    productId: number
    productTitle: string
    sizeLabel: string
    basePrice: number
    quantity: number
    image: string
    requiresReferences?: boolean
}

const SIZE_UNIT_STORAGE_KEY = 'size_unit_preference'
const CATEGORY_CARD_SIZES = '(max-width: 768px) 60vw, (max-width: 1280px) 26vw, 18vw'
const TRENDING_CARD_SIZES = '(max-width: 768px) 55vw, (max-width: 1280px) 24vw, 16vw'
const IMAGE_FORMAT_PRIORITY = ['avif', 'webp', 'jpeg', 'jpg', 'png', 'gif'] as const
const LOCAL_MEDIA_PATTERN = /(^\/media\/|chizhart\.com\/media)/i

const PRODUCT_TITLE_TRANSLATIONS: Record<string, Partial<Record<string, string>>> = {
    oil: {
        es: 'Retrato en pinturas al óleo',
        fr: "Portrait à la peinture à l'huile",
        de: 'Porträt in Ölfarben',
        uk: 'Портрет олійними фарбами',
    },
    ink: {
        es: 'Retrato en tinta',
        fr: "Portrait à l'encre",
        de: 'Tuscheporträt',
        uk: 'Портрет чорнилом',
    },
}
const PRODUCT_TITLE_KEYS: Record<string, string> = {
    oil: 'custom_pet_portrait_oil_painting',
    ink: 'custom_ink_painting',
}

const getImageFormatRank = (src: string) => {
    const match = src.match(/\.([a-z0-9]+)(?:\?|$)/i)
    if (!match) return IMAGE_FORMAT_PRIORITY.length + 1
    const idx = IMAGE_FORMAT_PRIORITY.indexOf(match[1].toLowerCase() as typeof IMAGE_FORMAT_PRIORITY[number])
    return idx === -1 ? IMAGE_FORMAT_PRIORITY.length + 1 : idx
}

const normalizeImageKey = (src: string) => {
    const [path] = src.split('?')
    const lastDot = path.lastIndexOf('.')
    return lastDot === -1 ? path : path.slice(0, lastDot)
}

const selectPreferredImageFormats = (sources: string[]) => {
    const order: string[] = []
    const selected = new Map<string, { url: string; rank: number }>()
    sources.forEach((src) => {
        const key = normalizeImageKey(src) || src
        const rank = getImageFormatRank(src)
        if (!selected.has(key)) {
            order.push(key)
            selected.set(key, { url: src, rank })
            return
        }
        const current = selected.get(key)!
        if (rank < current.rank) {
            selected.set(key, { url: src, rank })
        }
    })
    return order.map((key) => selected.get(key)!.url)
}

const convertLocalMediaToWebp = (url?: string | null) => {
  if (!url || !LOCAL_MEDIA_PATTERN.test(url) || /\.webp(\?|$)/i.test(url)) {
    return undefined
  }
  return url.replace(/\.jpe?g(\?.*)?$/i, '.webp$1')
}

const convertSrcSetToWebp = (srcSet?: string | null) => {
  if (!srcSet || !LOCAL_MEDIA_PATTERN.test(srcSet)) {
    return undefined
  }
  let didChange = false
  const converted = srcSet
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const firstSpace = entry.indexOf(' ')
      const url = firstSpace === -1 ? entry : entry.slice(0, firstSpace)
      const descriptor = firstSpace === -1 ? '' : entry.slice(firstSpace)
      const next = convertLocalMediaToWebp(url)
      if (next && next !== url) {
        didChange = true
        return `${next}${descriptor}`
      }
      return entry
    })
    .join(', ')
  return didChange ? converted : undefined
}

const getShapeLabel = (value: CanvasShapeOption, translate: (key: string, fallback: string) => string) => {
    const fallback = CANVAS_SHAPE_OPTIONS.find((option) => option.value === value)?.label ?? value
    return translate(`shape.${value}`, fallback)
}

const buildShapeLabelFallback = (label: string, shape: CanvasShapeOption) => {
    if (!label) return label
    const numericMatch = label.match(/[\d.,]+/)
    if (!numericMatch) return label
    const dimension = numericMatch[0].replace(/[^\d.,]/g, '')
    const remainder = label.slice((numericMatch.index ?? 0) + numericMatch[0].length)
    const unitMatch = remainder.match(/(cm|mm|in|ft)/i) ?? label.match(/(cm|mm|in|ft)/i)
    let unit = unitMatch ? unitMatch[0] : ''
    if (!unit && /["”']/u.test(label)) {
        unit = '"'
    }
    const suffix = unit ? (unit === '"' ? unit : ` ${unit}`) : ''
    if (shape === 'circle') {
        return `Ø ${dimension}${suffix}`.trim()
    }
    return `${dimension} × ${dimension}${suffix}`.trim()
}

const formatSizeLabel = (
    size: Product['sizes'][number],
    unit: 'imperial' | 'metric',
    shape?: CanvasShapeOption,
) => {
    if (!size) return ''
    const baseLabel =
        unit === 'metric'
            ? size.labelMetric ||
              convertImperialToMetricLabel(size.labelImperial || size.label || '') ||
              size.label
            : size.labelImperial || size.label || size.labelMetric || ''
    if (!shape) return baseLabel
    const variantEntry = size.variantLabels?.[shape]
    const variantLabel =
        unit === 'metric'
            ? variantEntry?.labelMetric?.trim()
            : variantEntry?.labelImperial?.trim()
    if (variantLabel) {
        return variantLabel
    }
    const hasPricing =
        Array.isArray(size.variantPricing?.[shape])
            ? (size.variantPricing?.[shape]?.length ?? 0) > 0
            : Boolean(size.variantPricing?.[shape])
    if (variantEntry?.enabled || hasPricing) {
        return buildShapeLabelFallback(baseLabel, shape)
    }
    return baseLabel
}

const resolveVariantPrice = (
    size: Product['sizes'][number] | undefined,
    shape?: CanvasShapeOption,
    thickness?: ShapeThicknessOption | null,
) => {
    if (!size) return 0
    if (!shape || !thickness) return size.price
    if (typeof thickness.price === 'number' && !Number.isNaN(thickness.price)) {
        return thickness.price
    }
    return size.price
}

const normalizeThicknessList = (
    size: Product['sizes'][number],
    shape: CanvasShapeOption,
): ShapeThicknessOption[] => {
    const entry = size.variantPricing?.[shape]
    if (!entry) return []
    if (Array.isArray(entry)) {
        return entry
            .map((option) => ({
                ...option,
                id: option.id || `${shape}-${option.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${option.price}`,
            }))
            .filter((option) => option.label && Number.isFinite(Number(option.price)))
    }
    if (typeof entry === 'object') {
        return Object.entries(entry).map(([key, value]) => ({
            id: key,
            label: typeof value === 'object' && value !== null && 'label' in value ? String(value.label) : key,
            price:
                typeof value === 'object' && value !== null && 'price' in value
                    ? Number((value as { price?: number }).price)
                    : Number(value),
        }))
    }
    return []
}

const shapeIsAvailableForSize = (size: Product['sizes'][number], shape: CanvasShapeOption) => {
    const shapePricing = normalizeThicknessList(size, shape)
    const hasPricing = shapePricing.length > 0
    const enabledFlag = size.variantLabels?.[shape]?.enabled
    return Boolean(hasPricing || enabledFlag)
}

type SlickArrowProps = {
    className?: string
    style?: React.CSSProperties
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    direction: 'prev' | 'next'
}

function SlickArrow({className, style, onClick, direction}: SlickArrowProps) {
    return (
        <button
            type="button"
            className={cn(
                'slick-arrow-custom absolute top-1/2 -translate-y-1/2 z-40 pointer-events-auto flex h-12 w-12 items-center justify-center text-primary transition hover:text-primary/80',
                direction === 'prev' ? 'left-4 sm:left-6 md:left-10' : 'right-4 sm:right-6 md:right-10',
                className,
            )}
            style={style}
            onClick={onClick}
            aria-label={direction === 'prev' ? 'Previous slide' : 'Next slide'}
        >
            {direction === 'prev' ? (
                <ArrowLeft className="h-6 w-6" strokeWidth={1}/>
            ) : (
                <ArrowRight className="h-6 w-6" strokeWidth={1}/>
            )}
        </button>
    )
}

interface HomePageProps {
    artworks: Artwork[]
    loading: boolean
    products: Product[]
    productsLoading: boolean
    brandCopy: typeof siteContent.brand
    fallbackCategories: { name: string; count: number }[]
    onCartOpen: () => void
    cartOpen?: boolean
}

export function FeaturedProductSection({
                                    product,
                                    onAddToCart,
                                    onAddToCartSilently,
                                    cartOpened = false,
                                }: {
    product: FeaturedProductConfig
    onAddToCart: (item: AddToCartPayload) => void
    onAddToCartSilently: (item: AddToCartPayload) => void
    cartOpened?: boolean
}) {
    if (!product.sizes || product.sizes.length === 0) {
        return null
    }
    const { language, t } = useTranslation()
    const { formatPrice: formatPriceFromBase } = useCurrency()
    const navigate = useNavigate()
    const [selectedShape, setSelectedShape] = useState<CanvasShapeOption>(DEFAULT_CANVAS_SHAPE)
    const [selectedSizeId, setSelectedSizeId] = useState<number | null>(null)
    const [selectedThicknessId, setSelectedThicknessId] = useState<string | null>(null)
    const [sizeUnit, setSizeUnit] = useState<'imperial' | 'metric'>('imperial')
    const [quantity, setQuantity] = useState(1)
    const [referenceUploads, setReferenceUploads] = useState<
        Array<{ name: string; url: string; path: string; size: number }>
    >([])
    const [uploadingRefs, setUploadingRefs] = useState(false)
    const storageModuleRef = useRef<Promise<typeof import('../lib/storage')> | null>(null)
    const [notice, setNotice] = useState<string | null>(null)
    const titleKey = PRODUCT_TITLE_KEYS[product.slug] || ''
    const productTitle =
        PRODUCT_TITLE_TRANSLATIONS[product.slug]?.[language]?.trim() ||
        (titleKey ? t(titleKey, product.title) : t(product.title, product.title))
    const productImages = useMemo(() => {
        const fallbackList = product.images?.length ? product.images : [product.baseImage]
        const sanitized = fallbackList.filter((src): src is string => Boolean(src))
        return selectPreferredImageFormats(sanitized)
    }, [product.baseImage, product.images])
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false)
    const [activeImageIndex, setActiveImageIndex] = useState(0)
    const responsiveProductImages = useMemo(
        () =>
            productImages.map((src) => ({
                raw: src,
                sources: buildArtworkImageSources(src),
            })),
        [productImages],
    )
    const viewerItems = responsiveProductImages.map((item, idx) => ({
        src: item.sources.original,
        title: `${product.title} preview ${idx + 1}`,
    }))
    const sliderRef = useRef<Slider | null>(null)
    const sizeRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const sizeRefreshInitialized = useRef(false)
    const [sizeRefreshActive, setSizeRefreshActive] = useState(false)
    const sliderAutoplayEnabled = product.sliderAutoplayEnabled !== false
    useEffect(() => {
        const slider = sliderRef.current
        if (!slider) return
        if (imagePreviewOpen || !sliderAutoplayEnabled || productImages.length <= 1) {
            slider.slickPause()
            return
        }
        slider.slickPlay()
    }, [imagePreviewOpen, productImages.length, sliderAutoplayEnabled])
    const sliderSpeed = product.sliderSpeed && product.sliderSpeed > 0 ? product.sliderSpeed : 800
    const sliderAutoplaySpeed =
        product.sliderAutoplaySpeed && product.sliderAutoplaySpeed > 0 ? product.sliderAutoplaySpeed : 5000
  const sliderMemoKey = `${product.slug}-${sliderSpeed}-${sliderAutoplaySpeed}-${sliderAutoplayEnabled ? '1' : '0'}-${productImages.length}`
    const sliderSettings: Settings = {
        dots: productImages.length > 1,
        infinite: productImages.length > 1,
        speed: sliderSpeed,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
        arrows: productImages.length > 1,
        prevArrow: <SlickArrow direction="prev"/>,
        nextArrow: <SlickArrow direction="next"/>,
        autoplay: productImages.length > 1 && sliderAutoplayEnabled,
        autoplaySpeed: sliderAutoplaySpeed,
        pauseOnHover: true,
        pauseOnFocus: true,
        swipe: true,
        draggable: true,
        beforeChange: (_current, next) => {
            setActiveImageIndex(next)
        },
    }
    const sizes = product.sizes
    const showCanvasOptions = product.slug !== 'ink'
    const shapeAvailability = useMemo<Record<CanvasShapeOption, boolean>>(
        () => {
            const availability = {} as Record<CanvasShapeOption, boolean>
            CANVAS_SHAPE_OPTIONS.forEach(({ value }) => {
                availability[value] = sizes.some((size) => shapeIsAvailableForSize(size, value))
            })
            return availability
        },
        [sizes],
    )
    const filteredSizes = useMemo(() => {
        if (!showCanvasOptions) return sizes
        return sizes.filter((size) => shapeIsAvailableForSize(size, selectedShape))
    }, [sizes, selectedShape, showCanvasOptions])
    const currentSize =
        (selectedSizeId && filteredSizes.find((size) => size.id === selectedSizeId)) ||
        filteredSizes[0] ||
        null
    const thicknessOptions = useMemo<ShapeThicknessOption[]>(() => {
        if (!showCanvasOptions || !currentSize) return []
        return normalizeThicknessList(currentSize, selectedShape)
    }, [showCanvasOptions, currentSize, selectedShape])
    const showThicknessOptions = showCanvasOptions && thicknessOptions.length > 0
    const selectedThicknessOption = thicknessOptions.find((option) => option.id === selectedThicknessId) || null
    const variantPrice = resolveVariantPrice(
        currentSize || undefined,
        showCanvasOptions ? selectedShape : undefined,
        showThicknessOptions ? selectedThicknessOption : null,
    )
    const displaySizeLabel = currentSize
        ? formatSizeLabel(currentSize, sizeUnit, showCanvasOptions ? selectedShape : undefined)
        : ''
    const selectionDescriptorParts = [
        showCanvasOptions ? getShapeLabel(selectedShape, t) : null,
        displaySizeLabel,
        showThicknessOptions && selectedThicknessOption ? `${selectedThicknessOption.label}` : null,
    ].filter(Boolean)
    const selectionDescriptor = selectionDescriptorParts.join(' · ')
    useEffect(() => {
        setSelectedShape(DEFAULT_CANVAS_SHAPE)
        setSelectedThicknessId(null)
        setSelectedSizeId(null)
    }, [product.id])
    useEffect(() => {
        if (!showCanvasOptions) return
        if (shapeAvailability[selectedShape]) return
        const fallback = CANVAS_SHAPE_OPTIONS.find(({ value }) => shapeAvailability[value])
        if (fallback) {
            setSelectedShape(fallback.value)
        }
    }, [showCanvasOptions, selectedShape, shapeAvailability])
    useEffect(() => {
        if (!filteredSizes.length) {
            setSelectedSizeId(null)
            return
        }
        if (!selectedSizeId || !filteredSizes.some((size) => size.id === selectedSizeId)) {
            setSelectedSizeId(filteredSizes[0].id)
        }
    }, [filteredSizes, selectedSizeId])
    useEffect(() => {
        if (typeof window === 'undefined') return
        const stored = window.localStorage.getItem(SIZE_UNIT_STORAGE_KEY)
        if (stored === 'metric' || stored === 'imperial') {
            setSizeUnit(stored)
        }
    }, [])
    useEffect(() => {
        if (typeof window === 'undefined') return
        window.localStorage.setItem(SIZE_UNIT_STORAGE_KEY, sizeUnit)
    }, [sizeUnit])
    useEffect(() => {
        if (!sizeRefreshInitialized.current) {
            sizeRefreshInitialized.current = true
            return
        }
        setSizeRefreshActive(true)
        if (sizeRefreshTimeoutRef.current) {
            clearTimeout(sizeRefreshTimeoutRef.current)
        }
        sizeRefreshTimeoutRef.current = setTimeout(() => {
            setSizeRefreshActive(false)
        }, 280)
        return () => {
            if (sizeRefreshTimeoutRef.current) {
                clearTimeout(sizeRefreshTimeoutRef.current)
            }
        }
    }, [sizeUnit, selectedShape])
    useEffect(() => {
        return () => {
            if (sizeRefreshTimeoutRef.current) {
                clearTimeout(sizeRefreshTimeoutRef.current)
            }
        }
    }, [])
    useEffect(() => {
        if (!showCanvasOptions || !showThicknessOptions) {
            setSelectedThicknessId(null)
            return
        }
        const firstOption = thicknessOptions[0]
        if (!selectedThicknessId || !thicknessOptions.some((option) => option.id === selectedThicknessId)) {
            setSelectedThicknessId(firstOption?.id ?? null)
        }
    }, [showCanvasOptions, showThicknessOptions, thicknessOptions, selectedThicknessId])
    const [sessionId] = useState(() => {
        const key = `reference_session_${product.slug}`
        const existing = localStorage.getItem(key)
        if (existing) return existing
        const generated = crypto.randomUUID()
        localStorage.setItem(key, generated)
        return generated
    })
    const trackProductEvent = (event: string, metadata?: Record<string, unknown>) => {
        trackAnalyticsEvent(event, {
            metadata: {
                productId: product.id,
                productSlug: product.slug,
                ...metadata,
            },
        })
    }

    const loadStorageModule = () => {
        if (!storageModuleRef.current) {
            storageModuleRef.current = import('../lib/storage')
        }
        return storageModuleRef.current
    }

    const handleQuantityChange = (delta: number) => {
        const newQty = quantity + delta
        if (newQty >= 1) {
            setQuantity(newQty)
            trackProductEvent('product_quantity_change', { nextQuantity: newQty })
        }
    }

    const processFiles = async (files: FileList | File[]) => {
        if (!files || referenceUploads.length >= 6) return
        const remaining = 6 - referenceUploads.length
        const toUpload = Array.from(files).slice(0, remaining)
        if (toUpload.length === 0) return
        setUploadingRefs(true)
        try {
            const { uploadFileToStorage } = await loadStorageModule()
            const uploads = await Promise.all(
                toUpload.map(async (file) => {
                    const uploaded = await uploadFileToStorage(file, `references/${product.slug}`)
                    await recordReferenceUpload({sessionId, productSlug: product.slug, url: uploaded.url})
                    trackProductEvent('reference_upload', { fileName: file.name, fileSize: file.size })
                    return {name: file.name, url: uploaded.url, path: uploaded.path, size: file.size}
                }),
            )
            setReferenceUploads((prev) => [...prev, ...uploads])
        } catch (err) {
            console.error(err)
        } finally {
            setUploadingRefs(false)
        }
    }

    const openImagePreview = (index: number) => {
        setActiveImageIndex(index)
        setImagePreviewOpen(true)
        trackProductEvent('product_image_zoom', { imageIndex: index })
    }

    const handleDialogChange = (open: boolean) => {
        setImagePreviewOpen(open)
        if (!open) {
            trackProductEvent('product_image_zoom_close', { imageIndex: activeImageIndex })
        }
    }

    const handleAddToCartClick = () => {
        if (!currentSize) return
        const displayLabel =
            selectionDescriptor || displaySizeLabel || currentSize.label || currentSize.labelImperial || ''
        onAddToCart({
            productId: product.id,
            productTitle: productTitle,
            sizeLabel: displayLabel,
            basePrice: variantPrice,
            quantity,
            image: product.baseImage,
            requiresReferences: true,
        })
        trackProductEvent('commission_add_to_cart', {
            sizeLabel: displayLabel,
            quantity,
            ...(showCanvasOptions
                ? {
                      shape: selectedShape,
                      thickness: selectedThicknessOption?.label ?? undefined,
                  }
                : {}),
        })
        setNotice(
            `${quantity} × ${displayLabel} saved in your cart. Open the cart anytime from the header to review or checkout.`,
        )
    }

    const handleBuyNow = () => {
        if (!currentSize) return
        const displayLabel =
            selectionDescriptor || displaySizeLabel || currentSize.label || currentSize.labelImperial || ''
        // Add item without opening the cart, then go straight to checkout
        onAddToCartSilently({
            productId: product.id,
            productTitle: productTitle,
            sizeLabel: displayLabel,
            basePrice: variantPrice,
            quantity,
            image: product.baseImage,
            requiresReferences: true,
        })
        trackProductEvent('commission_buy_now', {
            sizeLabel: displayLabel,
            quantity,
            ...(showCanvasOptions
                ? {
                      shape: selectedShape,
                      thickness: selectedThicknessOption?.label ?? undefined,
                  }
                : {}),
        })
        navigate('/checkout', { replace: false })
    }

  const isAvailable =
    product?.sizes?.some((size) => {
      if (typeof size.available === 'number') {
        return size.available > 0
      }
      if (typeof size.available === 'boolean') {
        return size.available
      }
      return true
    }) ?? true
  const heroImageSizes = '(min-width: 1280px) 40vw, (min-width: 1024px) 50vw, (min-width: 768px) 55vw, 60vw'
  const heroPreloadImage = responsiveProductImages[0]
  const heroPreloadHref =
    heroPreloadImage?.sources.variants?.w1200 ??
    heroPreloadImage?.sources.variants?.w800 ??
    heroPreloadImage?.sources.original
  const heroPreloadSrcSet = heroPreloadImage?.sources.srcSet
  const heroPreloadNeedsCrossOrigin = Boolean(heroPreloadHref && /^https?:\/\//i.test(heroPreloadHref))
  const heroPreloadWebpHref = convertLocalMediaToWebp(heroPreloadHref)
  const heroPreloadWebpSrcSet = convertSrcSetToWebp(heroPreloadSrcSet)
  const heroPreloadLinkHref = heroPreloadWebpHref ?? heroPreloadHref
  const heroPreloadLinkSrcSet = heroPreloadWebpSrcSet ?? heroPreloadSrcSet
  const heroPreloadLinkType = heroPreloadWebpHref ? 'image/webp' : undefined
  const heroImagesUseCrossOrigin = heroPreloadNeedsCrossOrigin

  return (
        <>
        <Helmet>
          {heroPreloadLinkHref && (
            <link
              rel="preload"
              as="image"
              href={heroPreloadLinkHref}
              imagesrcset={heroPreloadLinkSrcSet}
              imagesizes={heroImageSizes}
              fetchpriority="high"
              type={heroPreloadLinkType}
              crossOrigin={heroPreloadNeedsCrossOrigin ? 'anonymous' : undefined}
            />
          )}
          <link
            rel="preload"
            as="image"
            href="/logo-black.png"
            imagesrcset="/logo-black.png 512w"
            imagesizes="64px"
          />
        </Helmet>
        <section className={`bg-white py-12 md:py-20 px-4${isAvailable ? '' : ' opacity-80'}`}>
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                    <div className="flex items-start justify-center">
                        <div className="w-full max-w-md flex flex-col gap-6">
                            {responsiveProductImages.length > 0 ? (
                                <div className="relative overflow-visible">
                                    <Slider
                                        key={sliderMemoKey}
                                        ref={sliderRef}
                                        className="w-full"
                                        {...sliderSettings}
                                    >
                                        {responsiveProductImages.map(({ sources }, index) => (
                                            <div key={`${product.slug}-${index}`} className="px-1">
                                                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-transparent">
                                                    <ImageWithFallback
                                                        src={sources.src}
                                                        srcSet={sources.srcSet}
                                                        sizes={heroImageSizes}
                                                        placeholderSrc={sources.variants?.w400 ?? sources.src}
                                                        loading={index === 0 ? 'eager' : 'lazy'}
                                                        fetchPriority={index === 0 ? 'high' : 'auto'}
                                                        transitionEffect={index === 0 ? 'none' : 'fade'}
                                                        critical={index === 0}
                                                        decoding="async"
                                                        alt={`${buildProductAlt(productTitle)} — preview ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                        crossOrigin={heroImagesUseCrossOrigin ? 'anonymous' : undefined}
                                                        style={{
                                                            objectPosition: product.baseImagePosition || 'center center',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </Slider>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end pr-2">
                                        <button
                                            type="button"
                                            onClick={() => openImagePreview(activeImageIndex)}
                                            className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/95 text-primary shadow-xl border border-primary hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                            aria-label={`Open fullscreen view of ${productTitle}`}
                                        >
                                            <Maximize2 className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="aspect-[4/5] w-full rounded-2xl bg-muted"/>
                            )}

                        </div>
                    </div>

                    <div className="flex flex-col justify-center">
                        {product.eyebrow && (
                            <p className="text-foreground/60 uppercase tracking-[0.3em] text-xs text-center mb-2">
                                {product.eyebrow}
                            </p>
                        )}
                        <h1 className="text-4xl md:text-5xl mb-4">{productTitle}</h1>
                        <p className="text-lg text-foreground/70 mb-6" data-i18n-ignore="true">
                            {product.descriptionTranslations?.[language]?.trim() || product.description}
                        </p>
                        <Link
                            to="/reviews"
                            className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-border/70 px-4 py-2 text-sm font-semibold text-foreground/80 transition hover:border-primary hover:text-primary"
                        >
                            See Pet Owners Reviews
                            <ArrowRight className="h-4 w-4" />
                        </Link>

                        <div className="mb-4">
                            <div className="flex items-center gap-3">
                                <p className="text-3xl text-foreground mb-2" data-i18n-ignore="true">
                                {formatPriceFromBase(variantPrice)}
                            </p>
                                {!isAvailable && (
                                    <span className="inline-block rounded-full bg-[#E03121] text-white text-xs uppercase tracking-[0.3em] px-3 py-1">
                                        Sold Out
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-foreground/70">
                                <Package className="h-5 w-5 text-primary flex-shrink-0" />
                                <span>Free worldwide shipping.</span>
                            </div>
                        </div>

                        {showCanvasOptions && (
                        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="flex-1">
                                    <label className="block font-medium mb-3">Shape</label>
                                    <div className="rounded-3xl bg-[#fdf8f3] shadow-[0_12px_30px_rgba(82,63,45,0.08)]">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-4 sm:py-5">
                                            {CANVAS_SHAPE_OPTIONS.map(({ value, label }) => {
                                                const isActive = selectedShape === value
                                                const isDisabled = !shapeAvailability[value]
                                                return (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        onClick={() => {
                                                            if (!isDisabled) setSelectedShape(value)
                                                        }}
                                            className={`flex min-h-[2.75rem] items-center justify-between gap-3 rounded-2xl border px-4 py-2 text-sm font-semibold transition-all ${
                                                            isDisabled
                                                                ? 'border-border/40 bg-muted text-foreground/40 cursor-not-allowed'
                                                                : isActive
                                                                    ? 'border-primary bg-primary/5 text-primary shadow-lg shadow-primary/20'
                                                                    : 'border-border/70 bg-white text-foreground/70 hover:border-primary/50'
                                                        }`}
                                                    >
                                                        <span className="flex items-center gap-3">
                                                            {value === 'circle' ? (
                                                                <Circle
                                                                    className={`h-5 w-5 ${
                                                                        isActive ? 'text-primary' : 'text-foreground/60'
                                                                    }`}
                                                                    strokeWidth={1.6}
                                                                />
                                                            ) : (
                                                                <Square
                                                                    className={`h-5 w-5 ${
                                                                        isActive ? 'text-primary' : 'text-foreground/60'
                                                                    }`}
                                                                    strokeWidth={1.6}
                                                                />
                                                            )}
                                                            {label}
                                                        </span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <label className="block font-medium mb-3">Board</label>
                                    {showThicknessOptions ? (
                                        <div className="rounded-3xl bg-white/95 shadow-[0_12px_30px_rgba(82,63,45,0.08)]">
                                            <div className="flex flex-col sm:flex-row gap-3 py-4 sm:py-5">
                                                {thicknessOptions.map((option) => {
                                                    const isActive = selectedThicknessId === option.id
                                                    return (
                                                        <button
                                                            key={option.id}
                                                            type="button"
                                                            onClick={() => setSelectedThicknessId(option.id)}
                                                            className={`flex min-h-[2.75rem] items-center justify-between gap-3 rounded-2xl border px-4 py-2 text-sm font-semibold transition-all ${
                                                                isActive
                                                                    ? 'border-primary bg-primary/5 text-primary shadow-lg shadow-primary/20'
                                                                    : 'border-border/60 bg-white text-foreground/70 hover:border-primary/50'
                                                            }`}
                                                        >
                                                            <span className="flex items-center gap-3">
                                                                <Layers
                                                                    className={`h-5 w-5 ${
                                                                        isActive ? 'text-primary' : 'text-foreground/60'
                                                                    }`}
                                                                    strokeWidth={1.8}
                                                                />
                                                                <span className="block font-semibold text-left" data-i18n-ignore="true">
                                                                    {option.label}
                                                                </span>
                                                            </span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    ) : currentSize ? (
                                        <div className="rounded-3xl bg-white/95 sm:p-5 p-4 shadow-[0_8px_20px_rgba(82,63,45,0.07)] text-sm text-foreground/70">
                                            Fixed board profile for this selection—optimized for stability.
                                        </div>
                                    ) : (
                                        <div className="rounded-3xl bg-white/80 sm:p-5 p-4 text-sm text-foreground/60">
                                            Select a size to view board options.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mb-6">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <label className="block font-medium">Size</label>
                                <div className="inline-flex items-center rounded-full border border-border bg-white text-xs font-semibold">
                                    {(['imperial', 'metric'] as const).map((unit) => (
                                        <button
                                            key={unit}
                                            type="button"
                                            onClick={() => setSizeUnit(unit)}
                                            className={`px-3 py-1 rounded-full transition ${
                                                sizeUnit === unit
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'text-foreground/70'
                                            }`}
                                        >
                                            {unit === 'imperial' ? 'in' : 'cm'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {filteredSizes.map((size, index) => {
                                    const isActive = size.id === selectedSizeId
                                    return (
                                        <button
                                            key={`${product.id}-${size.id}`}
                                            onClick={() => setSelectedSizeId(size.id)}
                                            className={cn(
                                                'block w-full px-3 py-2 rounded-lg border transition-all text-sm text-left',
                                                isActive
                                                    ? 'border-primary bg-primary/5 text-primary'
                                                    : 'border-border hover:border-primary/50',
                                                sizeRefreshActive && 'size-refresh-item',
                                            )}
                                            style={sizeRefreshActive ? { animationDelay: `${index * 40}ms` } : undefined}
                                        >
                                            <span
                                                className="block w-full whitespace-normal break-words break-all leading-snug"
                                                data-i18n-ignore="true"
                                            >
                                              {formatSizeLabel(
                                                size,
                                                sizeUnit,
                                                showCanvasOptions ? selectedShape : undefined,
                                              )}
                                            </span>
                                        </button>
                                    )
                                })}
                                {!filteredSizes.length && (
                                    <div className="col-span-full text-sm text-foreground/60">
                                No sizes available for {getShapeLabel(selectedShape, t)} yet.
                                    </div>
                                )}
                            </div>
                        </div>

                        
                        {/*<div className="mb-6">*/}
                        {/*    <label className="block mb-3">Quantity</label>*/}
                        {/*    <div className="flex items-center gap-3">*/}
                        {/*        <button*/}
                        {/*            onClick={() => handleQuantityChange(-1)}*/}
                        {/*            className="w-10 h-10 flex items-center justify-center border border-border rounded-lg hover:bg-secondary transition-colors"*/}
                        {/*            disabled={quantity <= 1}*/}
                        {/*        >*/}
                        {/*            <Minus className="h-4 w-4"/>*/}
                        {/*        </button>*/}
                        {/*        <input*/}
                        {/*            type="number"*/}
                        {/*            value={quantity}*/}
                        {/*            onChange={(e) => {*/}
                        {/*                const val = parseInt(e.target.value)*/}
                        {/*                if (val >= 1) setQuantity(val)*/}
                        {/*            }}*/}
                        {/*            className="w-20 h-10 text-center border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"*/}
                        {/*        />*/}
                        {/*        <button*/}
                        {/*            onClick={() => handleQuantityChange(1)}*/}
                        {/*            className="w-10 h-10 flex items-center justify-center border border-border rounded-lg hover:bg-secondary transition-colors"*/}
                        {/*        >*/}
                        {/*            <Plus className="h-4 w-4"/>*/}
                        {/*        </button>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        <div className="space-y-3 mb-6">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                    style={{ height: "56px" }}
                                    className="h-12 bg-white text-foreground border-2 border-border hover:bg-secondary flex-1 min-w-[160px]"
                                    // size="lg"
                                    onClick={handleAddToCartClick}
                                    disabled={!currentSize}
                                >
                                    Add to cart
                                </Button>
                                <Button
                                    style={{ height: "56px" }}
                                    className="h-12 flex-1 min-w-[160px]"
                                    // size="lg"
                                    onClick={handleBuyNow}
                                    disabled={!currentSize}
                                >
                                    Add Photo & Buy
                                </Button>
                            </div>
                            {!cartOpened && notice && (
                                <p className="text-sm text-foreground/60">{notice}</p>
                            )}
                        </div>

                        <Dialog open={imagePreviewOpen} onOpenChange={handleDialogChange}>
                            <DialogContent
                                overlayClassName="bg-black/80 backdrop-blur-sm"
                                className="max-w-6xl w-[min(95vw,1100px)] border-none bg-transparent shadow-none p-0"
                                hideCloseButton
                            >
                                {viewerItems.length ? (
                                    <>
                                        <VisuallyHidden>
                                <DialogTitle>{productTitle} preview</DialogTitle>
                                            <DialogDescription>High-resolution artwork view</DialogDescription>
                                        </VisuallyHidden>
                                        <NanoGalleryViewer items={viewerItems} initialIndex={activeImageIndex} />
                                        <div className="flex justify-end mt-4 px-2">
                                            <DialogClose asChild>
                                                <button
                                                    type="button"
                                                    className="text-primary font-semibold hover:underline"
                                                >
                                                    Close preview
                                                </button>
                                            </DialogClose>
                                        </div>
                                    </>
                                ) : (
                                    <div className="bg-white rounded-2xl p-6 text-center text-foreground/70">
                                        <p>No image selected.</p>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </div>
        </section>
        </>
  )
}

export function HomePage({
                             artworks,
                             loading,
                             products,
                             productsLoading,
                             brandCopy,
                             fallbackCategories,
                             onCartOpen,
                             cartOpen = false,
}: HomePageProps) {
    const {addItem} = useCart()
    const { t } = useTranslation()
    const { formatPrice: formatPriceFromBase } = useCurrency()
    const [audienceCountry, setAudienceCountry] = useState(() => getLastKnownCountry() || '')
    const formatCountryName = useMemo(() => {
        if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined') {
            const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
            return (code?: string | null) => {
                if (!code) return ''
                const normalized = code.includes('@') ? code.split('@')[0] : code
                return displayNames.of(normalized) || normalized
            }
        }
        return (code?: string | null) => code || ''
    }, [])
    const categories =
        artworks.length > 0
            ? Array.from(new Set(artworks.map((art) => art.category)))
                .filter(Boolean)
                .slice(0, 3)
                .map((name) => ({
                    name,
                    count: artworks.filter((art) => art.category === name).length,
                }))
            : fallbackCategories

    const sortedProducts = [...products].sort((a, b) => {
        const order = ['oil', 'ink']
        const idxA = order.indexOf(a.slug)
        const idxB = order.indexOf(b.slug)
        return (idxA === -1 ? order.length : idxA) - (idxB === -1 ? order.length : idxB)
    })
    const [trendingArtworks, setTrendingArtworks] = useState<Artwork[]>([])
    const [trendingLoading, setTrendingLoading] = useState(false)
    const [trendingFetchReady, setTrendingFetchReady] = useState(false)
    const trendingSectionRef = useRef<HTMLDivElement | null>(null)
    const observeCategoryArtwork = useArtworkImpressions('home_category_cards')
    const observeTrendingArtwork = useArtworkImpressions('home_trending_cards')

    const handleAddToCart = (item: AddToCartPayload) => {
        addItem(item)
        onCartOpen()
    }

    const handleAddToCartSilently = (item: AddToCartPayload) => {
        addItem(item)
    }

    useEffect(() => {
        if (typeof window === 'undefined') return
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<string | undefined>).detail || ''
            setAudienceCountry(detail || '')
        }
        window.addEventListener('artist-country-update', handler as EventListener)
        return () => {
            window.removeEventListener('artist-country-update', handler as EventListener)
        }
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return
        let cancelled = false
        let fallbackTimer: number | null = null
        const markReady = () => {
            if (cancelled) return
            if (fallbackTimer !== null) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }
            setTrendingFetchReady(true)
        }

        // Always start a fallback timer so trending loads even if never scrolled near.
        fallbackTimer = window.setTimeout(markReady, 3500)

        if (typeof IntersectionObserver === 'undefined') {
            const cancel = deferToLoadAndIdle(markReady, 2200, 1200)
            return () => {
                cancelled = true
                if (fallbackTimer !== null) clearTimeout(fallbackTimer)
                cancel()
            }
        }

        const target = trendingSectionRef.current
        if (!target) {
            const cancel = deferToLoadAndIdle(markReady, 2400, 1400)
            return () => {
                cancelled = true
                if (fallbackTimer !== null) clearTimeout(fallbackTimer)
                cancel()
            }
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0]
                if (entry?.isIntersecting) {
                    markReady()
                    observer.disconnect()
                }
            },
            { rootMargin: '1200px 0px 1200px 0px', threshold: 0 },
        )
        observer.observe(target)
        return () => {
            cancelled = true
            if (fallbackTimer !== null) clearTimeout(fallbackTimer)
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        if (!trendingFetchReady) return
        if (!artworks.length) {
            setTrendingArtworks([])
            setTrendingLoading(false)
            return
        }
        let active = true
        const cancelIdle = deferToLoadAndIdle(() => {
            if (!active) return
            setTrendingLoading(true)
            fetchTrendingAnalytics(audienceCountry || undefined)
                .then((items) => {
                    if (!active) return
                    const lookup = new Map(artworks.map((artwork) => [artwork.id, artwork]))
                    const resolved = items
                        .map((item) => lookup.get(item.artworkId))
                        .filter((artwork): artwork is Artwork => Boolean(artwork))
                    setTrendingArtworks(resolved)
                    setTrendingLoading(false)
                    if (resolved.length) {
                        trackAnalyticsEvent('trending_section_loaded', {
                            metadata: { country: audienceCountry || 'UNKNOWN', total: resolved.length },
                        })
                    }
                })
                .catch(() => {
                    if (active) {
                        setTrendingLoading(false)
                    }
                })
        }, 2200, 1200)
        return () => {
            active = false
            cancelIdle()
        }
    }, [artworks, audienceCountry, trendingFetchReady])


    return (
        <>
        <Seo
          title={{
            en: 'Custom Pet Portraits in Ink and Oil — Hand-Painted From Your Photos',
            fr: 'Portraits personnalisés en encre et huile — peints à la main à partir de vos photos',
            de: 'Personalisierte Tierporträts in Tinte und Öl – handgemalt nach Ihren Fotos',
            es: 'Retratos de mascotas personalizados en tinta y óleo — pintados a mano desde tus fotos',
            uk: 'Персональні портрети домашніх улюбленців тушшю та олією — намальовані з ваших фото',
          }}
          description={{
            en: 'Order a custom oil portrait of your pet. Hand-painted from your photos with museum-quality realism. Worldwide shipping.',
            fr: 'Commandez un portrait à l’huile personnalisé de votre animal. Peint à la main à partir de vos photos avec un réalisme digne d’un musée. Livraison dans le monde entier.',
            de: 'Bestellen Sie ein individuelles Ölporträt Ihres Haustiers. Handgemalt nach Ihren Fotos mit musealer Detailtreue. Weltweiter Versand.',
            es: 'Encarga un retrato al óleo personalizado de tu mascota. Pintado a mano desde tus fotos con detalle de calidad de museo. Envío mundial.',
            uk: 'Замовте індивідуальний портрет вашого улюбленця олією. Розписано вручну з ваших фото з музейною точністю. Доставка по всьому світу.',
          }}
          canonicalPath="/"
        />
        <div className="min-h-screen">
            <div className="h-4 md:h-5 bg-white" />
            <h2 className="text-5xl md:text-6xl lg:text-7xl text-center bg-white">Custom Pet Portraits</h2>
            {sortedProducts.map((product) => (
                <FeaturedProductSection
                    key={`${product.id}-${product.slug}`}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onAddToCartSilently={handleAddToCartSilently}
                    cartOpened={cartOpen}
                />
            ))}
            {productsLoading && (
                <p className="text-center text-foreground/60 py-4">Syncing latest commission options…</p>
            )}


            <section className="relative bg-gradient-to-b from-white to-background py-20 px-4">
                <div className="max-w-5xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl lg:text-7xl mb-6">{brandCopy.heroTitle}</h1>
                    <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto leading-relaxed">
                        {brandCopy.heroSubtitle}
                    </p>
                    <p className="text-base md:text-lg text-foreground/70 max-w-3xl mx-auto mt-4">
                        Every custom pet portrait is a hand-painted oil portrait from your photo, created to capture your pet’s true personality. It’s a unique gift for pet lovers and pet parents — crafted with care and shipped worldwide.
                    </p>
                    <div className="h-2 md:h-5" />
                    <p className="text-base md:text-lg text-foreground/70 max-w-3xl mx-auto mt-4">
                        From dog portrait paintings and cat portrait paintings to pet memorial portraits and bespoke custom animal artwork, I create each realistic pet painting with layered texture and archival-quality pigments for long-lasting color.
                    </p>
                    <div className="h-4 md:h-5" />
                    <div className="h-4 md:h-5" />
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/commission"
                            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                        >
                            Commission a pet portrait
                        </Link>
                        <Link
                            to="/artworks"
                            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground hover:bg-white transition-colors"
                        >
                            View my artworks
                        </Link>
                    </div>
                </div>
            </section>

            {/*<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">*/}
            {/*    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">*/}
            {/*        {loading && <p className="text-center text-foreground/60">Loading latest artworks…</p>}*/}
            {/*        {!loading &&*/}
            {/*            artworks.slice(0, 9).map((artwork) => {*/}
            {/*                const coverImage = getPrimaryArtworkImage(artwork)*/}
            {/*                const imageSources = buildArtworkImageSources(coverImage)*/}
            {/*                return (*/}
            {/*                    <Link*/}
            {/*                        key={artwork.id}*/}
            {/*                        to={`/artworks/${artwork.id}`}*/}
            {/*                        className="group relative overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-xl transition-all duration-300"*/}
            {/*                    >*/}
            {/*                    <div className="aspect-[3/4] overflow-hidden">*/}
            {/*                        {coverImage ? (*/}
            {/*                            <ImageWithFallback*/}
            {/*                                src={imageSources.src}*/}
            {/*                                srcSet={imageSources.srcSet}*/}
            {/*                                sizes={imageSources.sizes}*/}
            {/*                                loading="lazy"*/}
            {/*                                transitionEffect="blur"*/}
            {/*                                alt={buildArtworkAlt(artwork.title)}*/}
            {/*                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"*/}
            {/*                            />*/}
            {/*                        ) : (*/}
            {/*                            <div className="w-full h-full bg-muted flex items-center justify-center text-sm text-muted-foreground">*/}
            {/*                                No image*/}
            {/*                            </div>*/}
            {/*                        )}*/}
            {/*                        </div>*/}
            {/*                        <div*/}
            {/*                            className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">*/}
            {/*                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">*/}
            {/*                                <h3 className="text-xl mb-1">{artwork.title}</h3>*/}
            {/*                                <p className="text-lg opacity-90">{formatPriceFromBase(artwork.price)}</p>*/}
            {/*                            </div>*/}
            {/*                        </div>*/}
            {/*                    </Link>*/}
            {/*                )*/}
            {/*            })}*/}
            {/*    </div>*/}
            {/*</section>*/}

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
                {categories.slice(0, 3).map((category) => (
                    <div key={category.name}>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-3xl mb-1">{category.name}</h2>
                                <p className="text-foreground/80">
                                  {category.count}{' '}
                                  {category.count === 1 ? 'artwork' : 'artworks'}
                                </p>
                            </div>
                            <Link
                                  to={`/artworks?category=${encodeURIComponent(category.name)}`}
                                  className="flex items-center gap-2 text-primary hover:gap-3 transition-all">
                                View all
                                <ArrowRight className="h-5 w-5"/>
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {artworks
                                .filter((art) => art.category === category.name)
                                .slice(0, 3)
                                .map((artwork) => {
                                    const coverImage = getPrimaryArtworkImage(artwork)
                                    const imageSources = buildArtworkImageSources(coverImage)
                                    const isSold = !artwork.available
                                    return (
                                        <Link
                                            key={artwork.id}
                                            to={`/artworks/${artwork.id}`}
                                            ref={observeCategoryArtwork(artwork.id)}
                                            className="group relative overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-xl transition-all duration-300"
                                        >
                                            <div className="relative aspect-[4/5] overflow-hidden">
                                                {coverImage ? (
                                                    <ImageWithFallback
                                                        src={imageSources.src}
                                                        srcSet={imageSources.srcSet}
                                                        sizes={CATEGORY_CARD_SIZES}
                                                        loading="lazy"
                                                        transitionEffect="blur"
                                                        alt={buildArtworkAlt(artwork.title)}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-muted flex items-center justify-center text-sm text-muted-foreground">
                                                        No image
                                                    </div>
                                                )}
                                                {isSold && (
                                                    <>
                                                        <div className="absolute inset-0 bg-[#E03121]/80 pointer-events-none" />
                                                        <div className="absolute top-3 right-3 z-10">
                                                            <Badge
                                                                variant="secondary"
                                                                className="text-white"
                                                                style={{ backgroundColor: '#E03121', borderColor: '#E03121' }}
                                                            >
                                                                Sold Out
                                                            </Badge>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <h3 className="mb-1">{artwork.title}</h3>
                                                <p className={isSold ? 'text-white/80' : 'text-foreground/70'}>
                                                    {formatPriceFromBase(artwork.price)}
                                                </p>
                                            </div>
                                        </Link>
                                    )
                                })}
                        </div>
                    </div>
                ))}
            </section>

            <div ref={trendingSectionRef} className="h-px w-full" aria-hidden />

            {trendingLoading && artworks.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-foreground/80">
                    Surfacing trending artworks near you…
                </div>
            )}

            {!trendingLoading && trendingArtworks.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-6">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h2 className="text-3xl">Trending</h2>
                            <div className="h-3" />
                            <p className="text-foreground/80">
                              {audienceCountry
                                ? t('home.trendingContextWithCountry', {
                                    country: formatCountryName(audienceCountry),
                                  })
                                : t('home.trendingContext')}
                            </p>
                        </div>
                        <Link
                            to="/artworks"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                        >
                            Browse all
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="h-6" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {trendingArtworks.filter((artwork) => artwork.available).map((artwork) => {
                            const coverImage = getPrimaryArtworkImage(artwork)
                            const sources = buildArtworkImageSources(coverImage)
                            const isSold = !artwork.available
                            return (
                                <Link
                                    key={`trending-${artwork.id}`}
                                    to={`/artworks/${artwork.id}`}
                                    ref={observeTrendingArtwork(artwork.id)}
                                    className="group relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm hover:shadow-lg transition-all"
                                >
                                    <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                                        {coverImage ? (
                                            <ImageWithFallback
                                                src={sources.src}
                                                srcSet={sources.srcSet}
                                                sizes={TRENDING_CARD_SIZES}
                                                loading="lazy"
                                                transitionEffect="blur"
                                                alt={buildArtworkAlt(artwork.title)}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center text-sm text-foreground/50">
                                                No image
                                            </div>
                                        )}
                                        {isSold && (
                                            <>
                                                <div className="absolute inset-0 bg-[#E03121]/80 pointer-events-none" />
                                                <div className="absolute top-3 right-3 z-10">
                                                    <Badge
                                                        variant="secondary"
                                                        className="text-white"
                                                        style={{ backgroundColor: '#E03121', borderColor: '#E03121' }}
                                                    >
                                                        Sold Out
                                                    </Badge>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="p-4 space-y-1">
                                        <h3 className="text-xl">{artwork.title}</h3>
                                        <p className="text-sm text-foreground/80">{artwork.category}</p>
                                        <p className="text-base font-semibold">{formatPriceFromBase(artwork.price)}</p>
                                    </div>
                                </Link>
                            )
                        })}
                    </div>
                </section>
            )}
        </div>
        </>
    )
}
